<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modal.title"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Sign')"
    :closeText="$t('Close')"
  >
    <div v-if="showImage">
      <v-row no-gutters>
        <v-col>
          <v-switch
            v-model="signature.useImage"
            v-bind:label="$t('UseUserSignatureImage')"
            color="primary"
          ></v-switch>
          <v-select
              :items="signatures"
              v-if="signature.useImage"
              v-model="selectedSignature"
              item-text="description"
              item-value="id"
              :label="$t('Signature')"
          ></v-select>
        </v-col>
        <v-row v-if="signature.useImage">
          <v-switch v-model="signature.includeFrame" v-bind:label="$t('IncludeFrame')" color="primary" class="mr-4"></v-switch>
          <v-switch v-model="signature.includeUserFullName" v-bind:label="$t('IncludeUserFullName')" color="primary" class="mr-4"></v-switch>
          <v-switch v-model="signature.includeTimeStamp" v-bind:label="$t('IncludeTimeStamp')" color="primary"></v-switch>
        </v-row>
      </v-row>
      <div v-show="signature.useImage">
        <div>
          <v-btn icon large @click="prevPage" v-show="hasPrev">
            <v-icon color="light-blue lighten-3">mdi-arrow-left</v-icon>
          </v-btn>
          <v-btn icon large @click="nextPage" v-show="hasNext">
            <v-icon color="light-blue lighten-3">mdi-arrow-right</v-icon>
          </v-btn>
          <span class="text-muted ml-1"
            >{{ $t("Page") }} {{ signature.image.page }} /{{ pageTotal }}</span
          >
        </div>
        <v-row justify="center" align="center" v-show="documentLoading">
          <div class="text-center">
            <div>
              <v-progress-circular
                class="my-4"
                :size="50"
                width="5"
                color="primary"
                indeterminate
              ></v-progress-circular>
            </div>
            <p class="display-1">{{ $t("Loading") }}...</p>
          </div>
        </v-row>
        <div id="document-map"></div>
        <div
          id="canvas-wrap"
          style="height: 630px; width: 485px; position: relative; overflow: hidden"
        >
          <canvas
            id="document-viewer"
            height="630"
            width="485"
            style="position: absolute"
          >
          </canvas>
          <vue-draggable-resizable
            :h="signature.image.height"
            :w="signature.image.width"
            :x="signature.image.x"
            :y="signature.image.y"
            @dragging="onDrag"
            @resizing="onResize"
            :parent="false"
            :minX="0"
            :minY="0"
            :maxX="pdfWidth - signature.image.width"
            :maxY="pdfHeight - signature.image.height"
            :ref="objectID"
            v-if="showDragResizeElement"
          >
            <img
              id="signer-image"
              ref="imgRef"
              :src="imgSrc"
              style="max-height: 500px; max-width: 500px"
            />
          </vue-draggable-resizable>
        </div>
      </div>
    </div>
    <div v-if="showCertificate">
      <v-row>
        <v-col md="7">
          <v-switch
            v-model="signature.useCertificate"
            v-bind:label="$t('UseUserCertificate')"
            color="primary"
            @change="useCertificateChanged"
          ></v-switch>
        </v-col>
        <v-col md="5">
          <v-text-field
            :disabled="!signature.useCertificate"
            v-bind:label="$t('Password')"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="showPassword = !showPassword"
            :type="showPassword ? 'text' : 'password'"
            v-model="signature.certificatePassword"
          ></v-text-field>
        </v-col>
      </v-row>
    </div>

  </base-modal>
</template>

<script>
import Utils from '@/ecm/common/Utils';
import DocumentService from '@/services/DocumentService';
import UserService from '@/services/UserService';
import SignatureData from '@/ecm/models/document/SignatureData';
import pdf from 'vue-pdf';
import VueDraggableResizable from 'vue-draggable-resizable';
import 'vue-draggable-resizable/dist/VueDraggableResizable.css';
import Signature from '../../../../../../../ecm/models/Signature';
let pdfDoc = null;

export default {
    components: { VueDraggableResizable },
    data() {
        return {
            modal: {
                title: this.$t('DigitalSignature'),
                key: 'SignatureModal',
            },
            open: false,
            pageRendering: false,
            documentLoading: false,
            pageNumPending: null,
            pageTotal: 1,
            pdfWidth: 0,
            pdfHeight: 0,
            showPassword: false,
            showImage: false,
            showCertificate: false,
            signature: new SignatureData(),
            selectedSignature: null,
            documentId: null,
            currentSignatureImage: null,
            showDragResizeElement: false,
            forcePdfLoad: false,
        };
    },
    computed: {
        hasPrev() {
            return this.pageTotal > 1 && this.signature.image.page != 1;
        },
        hasNext() {
            return this.pageTotal > 1 && this.signature.image.page != this.pageTotal;
        },
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        objectID() {
            return this.signature.documentId;
        },
        imgSrc() {
            return this.currentSignatureImage;
        }
    },
    methods: {
        onResize: function (x, y, width, height) {
            this.signature.image.x = x;
            this.signature.image.y = y;
            this.signature.image.width = width;
            this.signature.image.height = height;
            document.getElementById('signer-image').style.height = height + 'px';
            document.getElementById('signer-image').style.width = width + 'px';
        },
        onDrag: function (x, y) {
            this.signature.image.x = x;
            this.signature.image.y = y;
        },
        nextPage() {
            this.signature.image.page++;
            this.renderPage();
        },
        prevPage() {
            this.signature.image.page--;
            this.renderPage();
        },
        renderPage() {
            this.documentLoading = true;
            this.pageRendering = true;
            var self = this;

            // The page index might me greater than the current document doc page total.
            this.signature.image.page = this.signature.image.page > this.pageTotal ? this.pageTotal : this.signature.image.page;

            // Using promise to fetch the page
            pdfDoc.getPage(this.signature.image.page).then(function (page) {
                var scale = { scale: 1.1 };
                var pdfScale = {
                    scale:
                        document.getElementById('document-map').offsetWidth /
                        page.getViewport(scale).width,
                };

                var viewport = page.getViewport(pdfScale);

                var canvas = document.getElementById('document-viewer');
                var ctx = canvas.getContext('2d');
                self.pdfWidth = viewport.width;
                self.pdfHeight = viewport.height;
                canvas.height = viewport.height;
                canvas.width = viewport.width;
                document.getElementById('canvas-wrap').style.height =
                    canvas.height + 'px';
                document.getElementById('canvas-wrap').style.width =
                    canvas.width + 'px';
                var renderContext = {
                    canvasContext: ctx,
                    viewport: viewport,
                };
                var renderTask = page.render(renderContext);

                // Wait for rendering to finish
                renderTask.promise.then(function () {
                    self.documentLoading = false;
                    self.pageRendering = false;
                    if (self.pageNumPending !== null) {
                        // New page rendering is pending
                        self.renderPage(self.pageNumPending);
                        self.pageNumPending = null;
                    }

                    const signatureData = self.signatures.find(t => t.id === self.selectedSignature);

                    self.signature.includeFrame = signatureData.includeFrame;
                    self.signature.includeUserFullName = signatureData.includeUserFullName;
                    self.signature.includeTimeStamp = signatureData.includeTimeStamp;

                    if (signatureData.imageSizeX && signatureData.imageSizeY) {
                        const newImagePositionX = Math.round(signatureData.imagePositionX * self.pdfWidth / 100);
                        const positionY = signatureData.imagePositionY * self.pdfHeight / 100;
                        const newImagePositionY = Math.round(self.pdfHeight - positionY - signatureData.imageSizeY);
                        const imageSizeX = Math.round(signatureData.imageSizeX);
                        const imageSizeY = Math.round(signatureData.imageSizeY);

                        const visibleWidth = Math.min(newImagePositionX + imageSizeX, self.pdfWidth) - Math.max(newImagePositionX, 0);
                        const visibleHeight = Math.min(newImagePositionY + imageSizeY, self.pdfHeight) - Math.max(newImagePositionY, 0);
                        const visibleArea = visibleWidth * visibleHeight;
                        const totalArea = imageSizeX * imageSizeY;
                        const visiblePercentage = totalArea > 0 ? (visibleArea / totalArea) : 0;

                        if (visiblePercentage < 0.2) {
                            self.onResize(0, 0, imageSizeX, imageSizeY);
                        } else {
                            self.onResize(newImagePositionX, newImagePositionY, imageSizeX, imageSizeY);
                        }
                    } else {
                        self.onResize(0, 0, self.$refs.imgRef.offsetWidth, self.$refs.imgRef.offsetHeight);
                    }
                });
            });
        },
        loadPdf() {
            DocumentService.display(this.signature.documentId).then(
                (url) => {
                    const loadingTask = pdf.createLoadingTask(url).promise;

                    loadingTask.then((doc) => {
                        pdfDoc = doc;
                        this.pageTotal = doc.numPages;
                        this.renderPage();
                    });
                }
            );
        },
        showModal(documentId) {
            const currentUser = this.$store.getters['account/user'];

            this.forcePdfLoad = false;
            this.showCertificate = !!currentUser.SignatureCertificatePath;
            this.documentId = documentId;

            UserService.getSignaturesList().then((data) => {
                if (data.length > 0) {
                    for (let i = 0; i < data.length; i++)
                        data[i] = new Signature(data[i]);

                    this.signatures = data;

                    if (this.selectedSignature === null) {
                        const defaultSignature = this.signatures.find(t => t.isDefault === true);

                        this.selectedSignature = defaultSignature.id;
                    } else {
                        this.forcePdfLoad = true;
                    }

                    this.signature.documentId = documentId;

                    if (this.forcePdfLoad)
                        this.loadPdf();
                } else {
                    if (!this.showCertificate) {
                        Utils.toast.error(this.$t('DigitalSignatureConfigurationException'));
                        this.hide();
                        return;
                    }
                }
            });

            this.show();
        },
        show() {
            this.open = true;
        },
        hide() {
            this.open = false;
        },
        useCertificateChanged() {
            if (this.signature.useCertificate) {
                this.signature.useQualifiedSignature = false;
            }
        },
        useQualifiedSignatureChanged() {
            if (this.signature.useQualifiedSignature) {
                this.signature.useCertificate = false;
            }
        },
        saveItem() {
            this.modalInstance.progress(true);
            DocumentService.sign(this.signature.toDto(this.pdfWidth, this.pdfHeight))
                .then((result) => {
                    Utils.toast.success(
                        this.$t('Document') + ' ' + this.$t('SignedSuccessfully')
                    );
                    const oldObjectID = this.signature.documentId;
                    const newObjectID = result;
                    this.$emit('on-sign', { oldObjectID, newObjectID });
                    this.hide();
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.modalInstance.progress(false);
                });
        },
        changeUserSignatureImage(signatureId) {
            const currentUser = this.$store.getters['account/user'];

            UserService.displaySignatureImage(currentUser.Id, signatureId).then((url) => {
                this.currentSignatureImage = url;
                this.signature.selectedSignatureId = signatureId;
                this.signature.documentId = this.documentId;
                this.signature.useImage = this.showImage = true;

                if (this.forcePdfLoad)
                    this.loadPdf();

                this.showDragResizeElement = true;
            });
        },
    },
    watch: {
        selectedSignature(newVal, oldVal) {
            this.forcePdfLoad = true;
            this.changeUserSignatureImage(newVal);
        }
    }
};
</script>
<style scoped>
#document-viewer {
  -moz-box-shadow: 0 10px 48px rgba(30, 107, 174, 0.1),
    0 1px 1px rgba(255, 248, 254, 0.61);
  -webkit-box-shadow: 0 10px 48px rgba(30, 107, 174, 0.1),
    0 1px 1px rgba(255, 248, 254, 0.61);
  box-shadow: 0 10px 48px rgba(30, 107, 174, 0.1),
    0 1px 1px rgba(255, 248, 254, 0.61);
}
</style>
